// Page changes renumbered on the SERVER (14 Oct 2026; CUELIST_PAGE_CHANGE_SPEED_PLAN_2026-10-14.md,
// "Part B design"). The three page modals still save the copy and still send the PDF to the helper;
// then, on a show switched over, one call to functions applyPageChange moves every cue, note and
// Act/Scene mark, writes the labels, and lifts the lock last, instead of ~1,900 writes from this tab.
// Bookmarks stay here: they live on this user's own record and are one write.
//
// Switch: project.serverPageChange === true (set by hand on test shows; no UI). Absent = the
// browser path, unchanged.
import { waitForShowEditsSent } from './show-edits-sent.js'; // eslint-disable-line import/extensions

export function useServerPageChange(project) {
    return !!(project && project.serverPageChange === true);
}

// A refusal means the server wrote nothing; anything else may have stopped part way, and the
// server finishes the stored plan when the same opId comes back.
const REFUSALS = new Set(['failed-precondition', 'permission-denied', 'invalid-argument', 'unauthenticated', 'not-found']);
const isRefusal = err => !!(err && typeof err.code === 'string' && REFUSALS.has(err.code.replace(/^functions\//, '')));

// This show's unsent edits first (lib/show-edits-sent.js, bounded): the server reads Firestore, not this tab's store, so this tab's own
// recent edits (and the lock it just set) must have landed. `db` and `callable` are passed in so
// this can be tested without Firebase.
// A stop part way is retried with the same opId, up to TRIES calls in all with a short backoff
// (Josh, 14 Oct): the server finishes the stored plan. If every try fails the error is marked
// stoppedPartWay, and the modals leave the lock up (reopening the thumbnails or the owner's
// Close anyway clears it; the next page change on the show also finishes the stored plan).
const TRIES = 3;
const BACKOFF_MS = [1000, 3000];
export async function renumberPagesOnServer({
    db, callable, projectId, opId, op, params, pagesBefore,
    sleep = ms => new Promise(resolve => setTimeout(resolve, ms))
}) {
    await waitForShowEditsSent({ db, projectId });
    const payload = { projectId, opId, op, params, pagesBefore };
    for (let attempt = 1; ; attempt++) {
        try {
            // eslint-disable-next-line no-await-in-loop
            return (await callable(payload)).data;
        } catch (err) {
            if (isRefusal(err)) throw err;
            if (attempt >= TRIES) {
                if (err && typeof err === 'object') err.stoppedPartWay = true;
                throw err;
            }
            console.warn(`[page-change] try ${attempt} stopped; retrying with the same opId:`, err && err.message);
            // eslint-disable-next-line no-await-in-loop
            await sleep(BACKOFF_MS[attempt - 1]);
        }
    }
}

// The modals' bookmark rules, op by op: { moves: [{from, to}], removes: [page] } for moveBookmarksBulk.
export function bookmarkChanges(op, params, bookmarks) {
    const moves = [];
    const removes = [];
    for (const page of Object.keys(bookmarks || {})) {
        const n = Number(page);
        if (op === 'delete') {
            const count = (params.to - params.from) + 1;
            if (n >= params.from && n <= params.to) removes.push(page);
            else if (n > params.to) moves.push({ from: page, to: n - count });
        } else if (op === 'insert') {
            const shift = params.where === 'before' ? (params.into === 1 || n >= params.into) : n > params.into;
            if (shift) moves.push({ from: page, to: n + params.count });
        } else if (op === 'replace') {
            const offset = params.count - ((params.to - params.from) + 1);
            if (offset !== 0 && n > params.to) moves.push({ from: page, to: n + offset });
            else if (offset < 0 && n >= params.from + params.count) removes.push(page);
        }
    }
    return { moves, removes };
}


// WEBPACK FOOTER //
// ./src/lib/server-page-change.js